import { useState } from 'react';
import ImageUploading from 'react-images-uploading';
import DeleteIcon from "src/assets/all-icons/delete-icon.jsx";
import { v4 as uuidv4 } from 'uuid';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import PopupUpdateProfilePictureInSettings from '@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateProfilePictureInSettings';
import UserService from '@/services/user.service';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Text from '@/shared-components/text/components/Text';

import './styles/ProfileImage.css';

export default function ProfileImage({ userType, formState, setFormState, onImageChange, disableImageActions = false }) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const t = useTranslations('common.user_settings.edit_profile');
  const { state } = useStateHandler();
  const userFlags = state?.user_session?.user_flags;

  const actionsDisabled = disableImageActions || Boolean(formState.user_image?.loading);

  async function uploadProfileImage(image) {
    if (!image || actionsDisabled) {
      return;
    }

    setImageToCrop(image);
    setIsPopupOpen(true);
  }

  async function handleCroppedImage(croppedFile) {
    const temporalImageId = uuidv4();

    setFormState(prevValue => ({
      ...prevValue,
      user_image: {
        loading: true,
        temporalImageId,
        image_original_data: {
          name: croppedFile.name
        }
      }
    }));

    const { response, data } = await UserService.uploadImage(croppedFile);

    if (response.ok) {
      setFormState(prevValue => {
        if (Boolean(prevValue.user_image) && prevValue.user_image.temporalImageId === temporalImageId) {
          return {
            ...prevValue,
            user_image: data
          };
        } else {
          return prevValue;
        }
      });

      if (typeof onImageChange === "function") {
        onImageChange(data);
      }
    } else {
      setFormState(prevValue => ({
        ...prevValue,
        user_image: null
      }));

      alert(t('alerts.generic_error_contact', { email: 'contact@suarte.art' }));
    }
  }

  function handleDeleteImage() {
    if (actionsDisabled) {
      return;
    }

    setFormState(prevValue => ({
      ...prevValue,
      user_image: null
    }));

    if (typeof onImageChange === "function") {
      onImageChange(null);
    }
  }

  function handleProfilePictureError(errors) {
    if (errors.maxNumber) alert(t('profile_image.errors.max_number'));
    else if (errors.acceptType) alert(t('profile_image.errors.invalid_type'));
    else if (errors.maxFileSize) alert(t('profile_image.errors.max_file_size'));
  }

  const cropShape = userType === 'gallery' ? 'rect' : 'circle';

  return (
    <>
      <ImageUploading
        maxNumber={1}
        onChange={(imagesList) => uploadProfileImage(imagesList?.[0]?.file)}
        onError={handleProfilePictureError}
        maxFileSize={15000000}
      >
        {({ onImageUpload }) => (
          <div className="profile-image__block">
            <div className="profile-image__container">
              <UserProfileImage
                className="profile-image__image"
                image={formState.user_image?.image_id}
                typeOfProfile={userType}
                loading={formState.user_image?.loading}
                userFlags={userFlags}
              />

              {Boolean(formState.user_image) && <DeleteIcon className="profile-image__delete-image" onClick={handleDeleteImage} />}
            </div>

            <div className="profile-image__actions-block">
              <Text
                className="profile-image__upload-image element-non-selectable font-size-medium"
                onClick={() => { if (!actionsDisabled) onImageUpload(); }}>
                {
                  formState.user_image ?
                    t('profile_image.actions.change_picture')
                    :
                    t('profile_image.actions.add_picture')
                }
              </Text>
            </div>
          </div>
        )}
      </ImageUploading>
      {isPopupOpen && (
        <PopupUpdateProfilePictureInSettings
          open={isPopupOpen}
          image={URL.createObjectURL(imageToCrop)}
          exitCrop={() => setIsPopupOpen(false)}
          continueCrop={() => setIsPopupOpen(false)}
          setCroppedFile={handleCroppedImage}
          cropShape={cropShape}
        />
      )}
    </>
  );
}
